// La hoja "Skills" del menú "⋯" del Chat — ADR-2026-09-09-el-agente-tiene-skills.
//
// Dos grupos: las de la EMPRESA (las que el agente guardó cuando el usuario le enseñó
// un procedimiento; se pueden borrar, como una memoria, y se despliegan para releer lo
// que él mismo dictó) y las de FÁBRICA, que se listan por nombre y descripción NADA MÁS:
// el instructivo es interno (ADR-2026-09-12-el-instructivo-de-las-skills-de-fabrica-es-
// interno). Lo que el usuario necesita de ellas es saber qué sabe hacer el agente y con
// qué palabras pedirlo, y eso ya lo dice la descripción.

import React, { useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import type { SkillDelAgente } from '../../pages/v2/skills/skill';

const Aviso: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-3 py-2 text-[13px] leading-snug text-[#3C3C43]/60">{children}</p>
);

const Encabezado: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
    {children}
  </p>
);

const FilaSkill: React.FC<{
  skill: SkillDelAgente;
  /** Si se puede abrir para leer el instructivo. Sólo las de la empresa. */
  desplegable: boolean;
  onBorrar?: (id: string) => void;
}> = ({ skill, desplegable, onBorrar }) => {
  const [abierta, setAbierta] = useState(false);
  const titulo = (
    <>
      <span className="block text-[14px] font-medium text-brand-black leading-snug">
        {skill.nombre}
      </span>
      <span className="block text-[13px] text-[#3C3C43]/70 leading-snug mt-0.5">
        {skill.descripcion}
      </span>
    </>
  );
  return (
    <div className={`rounded-xl transition-colors ${desplegable ? 'hover:bg-neutral-50' : ''}`}>
      <div className="flex items-start gap-2 px-3 py-2.5">
        {desplegable ? (
          <button
            type="button"
            onClick={() => setAbierta((v) => !v)}
            aria-expanded={abierta}
            className="flex-1 min-w-0 text-left"
          >
            {titulo}
          </button>
        ) : (
          <div className="flex-1 min-w-0">{titulo}</div>
        )}
        {desplegable && (
          <button
            type="button"
            onClick={() => setAbierta((v) => !v)}
            aria-label={abierta ? 'Cerrar' : 'Ver el instructivo'}
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#3C3C43]/40 hover:text-brand-black hover:bg-[#02062B]/[0.06] transition-colors"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${abierta ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        )}
        {onBorrar && skill.id && (
          <button
            type="button"
            onClick={() => onBorrar(skill.id!)}
            aria-label="Borrar skill"
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#3C3C43]/40 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {desplegable && abierta && (
        <div className="px-3 pb-3">
          <p className="text-[12px] text-[#3C3C43]/60 leading-snug mb-2">
            <span className="font-medium">Cuándo la usa:</span> {skill.cuando}
          </p>
          {/* El cuerpo es markdown, pero acá se lee como texto: es un instructivo para
              el agente, no una página para el usuario. */}
          <pre className="whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-brand-black/85 bg-[#02062B]/[0.03] rounded-lg p-3 max-h-[320px] overflow-y-auto">
            {skill.cuerpo}
          </pre>
        </div>
      )}
    </div>
  );
};

/** Lista de skills: las de la empresa (con borrar) y las de fábrica (solo lectura). */
export const ListaSkills: React.FC<{
  deEmpresa: SkillDelAgente[];
  deFabrica: SkillDelAgente[];
  cargando: boolean;
  onBorrar: (id: string) => void;
}> = ({ deEmpresa, deFabrica, cargando, onBorrar }) => (
  <div className="flex flex-col gap-1">
    <Encabezado>Las que le enseñaste</Encabezado>
    {cargando ? (
      <Aviso>Cargando…</Aviso>
    ) : deEmpresa.length === 0 ? (
      <Aviso>
        Todavía ninguna. Cuando le expliques cómo querés que haga algo que se repite
        ("siempre que te pida fotos para Instagram, hacelas así"), el agente lo guarda
        acá y lo usa la próxima vez.
      </Aviso>
    ) : (
      deEmpresa.map((s) => (
        <FilaSkill key={s.id ?? s.nombre} skill={s} desplegable onBorrar={onBorrar} />
      ))
    )}
    <Encabezado>Las que ya sabe</Encabezado>
    {deFabrica.map((s) => (
      <FilaSkill key={s.nombre} skill={s} desplegable={false} />
    ))}
  </div>
);
